import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonGroup } from '@pattern-library/buttons';
import { WarningBoldIcon } from '@pattern-library/icon';
import TextInput from '@pattern-library/text-input';
import PasswordReveal from '@pattern-library/password-reveal';
import useUnifiedLoginFormValidation from '../../hooks/useUnifiedLoginFormValidation';
import useCapsLockDetection from '../../hooks/useCapsLockDetection';
import { getFingerprint } from '../../utilities/fingerprint';

const propTypes = {
    buttonProps: PropTypes.objectOf(PropTypes.string).isRequired,
    forgotPasswordUrl: PropTypes.string,
    forgotUsernameUrl: PropTypes.string,
    formAction: PropTypes.string.isRequired,
    loginType: PropTypes.string.isRequired,
    passwordRevealProps: PropTypes.objectOf(
        PropTypes.oneOfType([PropTypes.string, PropTypes.array])
    ).isRequired,
    returnQueryString: PropTypes.string,
    showNewUserLoginCta: PropTypes.bool,
    userExperience: PropTypes.oneOf([0, 1]),
    usernameInputProps: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.array]))
        .isRequired
};

function LoginForm(props) {
    const {
        buttonProps,
        forgotPasswordUrl,
        forgotUsernameUrl,
        formAction,
        loginType,
        passwordRevealProps,
        returnQueryString,
        showNewUserLoginCta = false,
        userExperience,
        usernameInputProps
    } = props;

    const { showCapsLockWarning } = useCapsLockDetection();
    const isSubmittingRef = useRef(false);

    const { validation: usernameValidateMethods, ...usernameProps } = usernameInputProps;
    const { validation: passwordValidateMethods, ...passwordProps } = passwordRevealProps;
    const username = usernameProps.name;
    const password = passwordProps.name;

    /**
     * @type { import('@pattern-library/base/dist/js/hooks/useFormValidation').FormValidationSchema}
     */
    const schema = {
        [username]: usernameValidateMethods,
        [password]: passwordValidateMethods
    };

    const { onSubmit, hasError, errorMessage } = useUnifiedLoginFormValidation({
        schema,
        onValidate: ({ isValid }) => {
            // useFormValidation always preventDefaults, then native-submits when valid.
            // Unlock only when client validation fails so the user can retry.
            if (!isValid) {
                isSubmittingRef.current = false;
            }
        }
    });

    const handleSubmit = event => {
        if (isSubmittingRef.current) {
            event.preventDefault();
            return;
        }

        // Lock before async validation so a rapid second click cannot start
        // another submit cycle (and another SRC after auth).
        isSubmittingRef.current = true;
        onSubmit(event);
    };

    return (
        <div
            className={`ul-login-form${showNewUserLoginCta ? ' ul-login-form--new-user-experience' : ''}`}
        >
            <form
                action={formAction}
                method="post"
                autoComplete="off"
                onSubmit={handleSubmit}
                noValidate
            >
                <input type="hidden" id="RetQs" name="RetQs" value={returnQueryString || ''} />
                <input
                    type="hidden"
                    id="UserExp"
                    name="UserExp"
                    value={userExperience ? `${userExperience}` : ''}
                />
                <input type="hidden" id="Type" name="Type" value={loginType} />
                <input
                    type="hidden"
                    id="DeviceFingerprint"
                    name="DeviceFingerprint"
                    value={getFingerprint()}
                />
                <TextInput
                    isRequired
                    autoComplete="off"
                    useEmbeddedLabel
                    hasError={hasError(username)}
                    errorMessage={errorMessage(username)}
                    {...usernameProps}
                />
                {showNewUserLoginCta && forgotUsernameUrl && (
                    <div className="ul-login-field-link asc-text--right asc-nmt-3 asc-mb-4">
                        <a
                            href={forgotUsernameUrl}
                            className="asc-btn asc-btn--link asc-btn--link-no-pad asc-p--small asc-mtb-0"
                        >
                            <u>Forgot username?</u>
                        </a>
                    </div>
                )}
                <PasswordReveal
                    isRequired
                    preventAutofill
                    useEmbeddedLabel
                    hasError={hasError(password)}
                    errorMessage={errorMessage(password)}
                    {...passwordProps}
                />
                {showNewUserLoginCta && forgotPasswordUrl && (
                    <div className="ul-login-field-link asc-text--right asc-nmt-3 asc-mb-4">
                        <a
                            href={forgotPasswordUrl}
                            className="asc-btn asc-btn--link asc-btn--link-no-pad asc-p--small asc-mtb-0"
                        >
                            <u>Forgot password?</u>
                        </a>
                    </div>
                )}
                {showCapsLockWarning && (
                    <div className="asc-form-help ul-login-form-caps-lock-warning asc-nmt-5">
                        <WarningBoldIcon
                            customSize="14px"
                            color="warning"
                            className="asc-form-help-icon"
                        />
                        <p className="asc-form-help--error-message">Caps Lock is on</p>
                    </div>
                )}
                <ButtonGroup
                    alignment="center"
                    block={showNewUserLoginCta}
                    className={showNewUserLoginCta ? 'asc-mt-3 asc-mb-0' : 'asc-mb-4 asc-mt-3'}
                >
                    <Button type="submit" className="asc-mtb-0" {...buttonProps} />
                </ButtonGroup>
            </form>
        </div>
    );
}

LoginForm.propTypes = propTypes;
export default LoginForm;
